import React, { useEffect } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import BottomNav from './BottomNav';
import { AudioProvider, useKiooAudio } from './AudioProvider';
import StationTheme from './StationTheme';
import StationPicker from './StationPicker';
import InstallAppButton from './InstallAppButton';
import ProgramReminders from './ProgramReminders';

const KIOO_LOGO =
  'https://customer-assets-cm19k8pv.emergentagent.net/job_dbf8fccb-0921-4831-b85d-23b248549992/artifacts/y687vkx5_KIOO%20RADIO.webp';

/** Slim top bar — reflects the currently selected station (name/logo/frequency/tagline).
 * The station logo is rendered on a transparent background and displays fully
 * within the header. A prominent Change-Station picker sits on the right. A
 * large translucent watermark of the station logo fills the header background
 * so branding is always visible at a glance.
 */
const StationTopBar = () => {
  const { currentStation } = useKiooAudio();
  const logo = currentStation?.logo_url || KIOO_LOGO;
  const name = currentStation?.name || 'Kioo Radio';
  const frequency = currentStation?.frequency || '98.1 FM';
  const tagline = currentStation?.tagline || 'The Gift of Good News';
  return (
    <header
      data-testid="mobile-app-header"
      className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b px-3 py-2 flex items-center gap-2 overflow-hidden relative"
      style={{ borderColor: 'color-mix(in srgb, var(--kioo-primary) 25%, white)' }}
    >
      {/* Big transparent watermark of the current station logo. */}
      <img
        src={logo}
        alt=""
        aria-hidden="true"
        data-testid="mobile-app-header-bg-logo"
        className="pointer-events-none select-none absolute right-2 top-1/2 -translate-y-1/2 h-[180%] w-auto object-contain opacity-10"
      />
      <Link
        to="/app"
        className="relative z-10 flex items-center gap-2 min-w-0 flex-1"
        data-testid="mobile-app-logo-link"
      >
        {/* Transparent, fully-visible logo (no clipping, no background fill). */}
        <img
          src={logo}
          alt={name}
          className="h-12 w-auto max-w-[64px] object-contain flex-shrink-0"
          style={{ background: 'transparent' }}
          loading="eager"
          data-testid="mobile-app-shell-logo"
        />
        <div className="leading-tight min-w-0">
          <div
            className="text-[13px] font-extrabold truncate"
            style={{ color: 'var(--kioo-primary-dark)' }}
            data-testid="mobile-app-shell-name"
          >
            {name}
          </div>
          <div
            className="text-[10px] text-gray-500 truncate"
            data-testid="mobile-app-shell-tagline"
          >
            {frequency} · {tagline}
          </div>
        </div>
      </Link>
      <div className="relative z-10 flex-shrink-0">
        <StationPicker variant="chip" />
      </div>
    </header>
  );
};

const MobileAppShell = () => {
  // Register PWA manifest for the mobile app
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = '/mobile-app-manifest.json';
    link.setAttribute('data-mobile-app', 'true');
    document.head.appendChild(link);

    const themeMeta = document.createElement('meta');
    themeMeta.name = 'theme-color';
    themeMeta.content = '#059669';
    themeMeta.setAttribute('data-mobile-app', 'true');
    document.head.appendChild(themeMeta);

    // Register the mobile-app service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/mobile-app-sw.js', { scope: '/app/' })
        .catch(() => {
          /* PWA registration best-effort */
        });
    }

    return () => {
      document
        .querySelectorAll('[data-mobile-app="true"]')
        .forEach((el) => el.remove());
    };
  }, []);

  return (
    <AudioProvider>
      <StationTheme>
      <Helmet>
        <title>Kioo Radio · 98.1 FM</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
        />
        <meta
          name="description"
          content="Kioo Radio 98.1 FM — Listen live, see what's on air, and send a voice message to the station. Serving Liberia, Sierra Leone, and Guinea."
        />
      </Helmet>

      <div
        data-testid="mobile-app-shell"
        className="min-h-screen flex flex-col"
        style={{
          background:
            'linear-gradient(to bottom, var(--kioo-background) 0%, #ffffff 40%, #fff7ed 100%)',
        }}
      >
        {/* Slim top bar — reflects the selected station */}
        <StationTopBar />

        <main
          data-testid="mobile-app-main"
          className="flex-1 pb-24 max-w-md w-full mx-auto"
        >
          <Outlet />
        </main>

        <InstallAppButton />
        <ProgramReminders />
        <BottomNav />
      </div>
      </StationTheme>
    </AudioProvider>
  );
};

export default MobileAppShell;
